/* eslint-disable react/jsx-no-bind */
import {
  TrendingUp,
  FilterList,
  Clear,
  Search,
  Assignment,
  DirectionsCar,
  LocalShipping,
  Scale,
  Person,
  Phone,
  Route,
  AccessTime,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  TextField,
  Button,
  Paper,
  TablePagination,
  CircularProgress,
  Divider,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { tokens } from '../../theme';
import { formatToIST } from '../../utils/dateFormatter';
import logger from '../../utils/logger';
import Header from '../Header';

import bookingIntelligenceService from './bookingIntelligenceService';

// Premium design tokens (shadows, radius) – theme-agnostic
const cardShadow = '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)';
const cardShadowHover =
  '0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)';
const radiusSm = 4;
const radiusMd = 6;
const radiusLg = 8;

const MetricCell = ({ label, value }) => (
  <Grid item xs={6}>
    <Typography
      variant="caption"
      color="text.secondary"
      display="block"
      sx={{
        fontSize: { xs: '0.7rem', sm: 'inherit' },
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight="600"
      sx={{
        fontSize: { xs: '0.8125rem', sm: 'inherit' },
        color: 'text.primary',
      }}
    >
      {value}
    </Typography>
  </Grid>
);

const LabelValue = ({ label, value, icon }) => (
  <Box
    display="flex"
    gap={0.75}
    alignItems="center"
    flexWrap="wrap"
    sx={{ py: 0.375 }}
  >
    {icon ? (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="body2"
          color="text.primary"
          title={value}
          sx={{
            wordBreak: 'break-word',
            flex: 1,
            minWidth: 0,
            fontWeight: 700,
          }}
        >
          {value || '—'}
        </Typography>
      </>
    ) : (
      <>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            flexShrink: 0,
            letterSpacing: '0.02em',
          }}
        >
          {label}:
        </Typography>
        <Typography
          variant="body2"
          color="text.primary"
          title={value}
          sx={{
            wordBreak: 'break-word',
            flex: 1,
            minWidth: 0,
            fontWeight: 700,
          }}
        >
          {value || '—'}
        </Typography>
      </>
    )}
  </Box>
);

/** Section card: icon + title, premium rounded block with subtle shadow */
const SectionCard = ({ icon, title, iconColor, children }) => (
  <Box
    sx={{
      p: { xs: 1.5, sm: 2 },
      borderRadius: radiusMd,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      height: '100%',
      boxShadow: cardShadow,
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      '&:hover': {
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        borderColor: 'action.selected',
      },
    }}
  >
    <Box
      display="flex"
      alignItems="center"
      gap={1.25}
      sx={{
        mb: 1.5,
        pb: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: radiusSm,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          color: iconColor,
          flexShrink: 0,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="subtitle2"
        fontWeight="700"
        color="text.primary"
        sx={{ letterSpacing: '0.01em' }}
      >
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const BookingIntelligence = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [matches, setMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignLoading, setAssignLoading] = useState({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [topFilters, setTopFilters] = useState({
    tripId: '',
    shipmentId: '',
  });
  const [filters, setFilters] = useState({
    minScore: '',
    maxScore: '',
    source: '',
    destination: '',
    vehicleType: '',
  });

  useEffect(() => {
    // Parse URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const tripId = searchParams.get('trip_id');
    const shipmentId = searchParams.get('shipment_id');

    if (tripId || shipmentId) {
      setTopFilters({
        tripId: tripId || '',
        shipmentId: shipmentId || '',
      });
    }

    fetchMatches(tripId, shipmentId, location.search !== '' && page === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, page, pageSize]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, filters]);

  const fetchMatches = async (
    tripId = null,
    shipmentId = null,
    resetPage = false,
  ) => {
    try {
      setLoading(true);
      const currentPage = resetPage ? 1 : page + 1; // API uses 1-based page
      if (resetPage) setPage(0);
      const response = await bookingIntelligenceService.fetchMatches(
        tripId,
        shipmentId,
        currentPage,
        pageSize,
      );
      const matches =
        response?.data?.matches ||
        response?.matches ||
        response?.data ||
        response ||
        [];
      const pagination = response?.data?.pagination;
      const total =
        pagination?.totalItems ||
        response?.data?.totalCount ||
        response?.totalCount ||
        matches.length;
      const tripDetails = response?.data?.tripDetails;
      const shipmentDetails = response?.data?.shipmentDetails;

      // Add tripDetails or shipmentDetails to each match if they exist
      const enrichedMatches = matches.map((match) => ({
        ...match,
        ...(tripDetails && { tripDetails }),
        ...(shipmentDetails && { shipmentDetails }),
      }));

      setMatches(enrichedMatches);
      setTotalCount(total);
    } catch (error) {
      logger.error('Error fetching matches:', error);
      setMatches([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSize(newSize);
    setPage(0);
  };

  const handleTopFilterSearch = () => {
    const { tripId, shipmentId } = topFilters;
    fetchMatches(tripId.trim() || null, shipmentId.trim() || null);
  };

  const handleTopFilterChange = (field, value) => {
    setTopFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearTopFilters = () => {
    setTopFilters({ tripId: '', shipmentId: '' });
    setPage(0);
    fetchMatches(null, null, true);
  };

  const getMatchIds = (match) => {
    const tripId =
      match.tripId ??
      match.trip_id ??
      match.tripDetails?.tripId ??
      match.tripDetails?.trip_id;
    const shipmentId =
      match.shipmentId ??
      match.shipment_id ??
      match.shipmentDetails?.shipmentId ??
      match.shipmentDetails?.shipment_id;
    return { tripId, shipmentId };
  };

  const handleAssign = async (match) => {
    const { tripId, shipmentId } = getMatchIds(match);

    if (!tripId || !shipmentId) return;

    const key = `${tripId}-${shipmentId}`;
    setAssignLoading((prev) => ({ ...prev, [key]: true }));

    try {
      await bookingIntelligenceService.assignMatch(tripId, shipmentId);
      toast.success('Match assigned successfully');
      clearFilters();
      clearTopFilters();
      setPage(0);
      // Refresh matches after assignment
      const { tripId: filterTripId, shipmentId: filterShipmentId } = topFilters;
      fetchMatches(
        filterTripId.trim() || null,
        filterShipmentId.trim() || null,
      );
    } catch (error) {
      logger.error('Assignment failed:', error);
      toast.error(error.response?.data?.message || 'Failed to assign match');
    } finally {
      setAssignLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const applyFilters = () => {
    let filtered = matches;

    if (filters.minScore) {
      filtered = filtered.filter(
        (match) => match.matchScore >= parseFloat(filters.minScore),
      );
    }
    if (filters.maxScore) {
      filtered = filtered.filter(
        (match) => match.matchScore <= parseFloat(filters.maxScore),
      );
    }
    if (filters.source) {
      filtered = filtered.filter(
        (match) =>
          match.tripSource
            .toLowerCase()
            .includes(filters.source.toLowerCase()) ||
          match.shipmentSource
            .toLowerCase()
            .includes(filters.source.toLowerCase()),
      );
    }
    if (filters.destination) {
      filtered = filtered.filter(
        (match) =>
          match.tripDest
            .toLowerCase()
            .includes(filters.destination.toLowerCase()) ||
          match.shipmentDest
            .toLowerCase()
            .includes(filters.destination.toLowerCase()),
      );
    }
    if (filters.vehicleType) {
      filtered = filtered.filter((match) =>
        match.vehicleName
          .toLowerCase()
          .includes(filters.vehicleType.toLowerCase()),
      );
    }

    setFilteredMatches(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      minScore: '',
      maxScore: '',
      source: '',
      destination: '',
      vehicleType: '',
    });
  };

  const getScoreColor = (score) => {
    if (score >= 90) return colors.greenAccent[500];
    if (score >= 70) return colors.blueAccent[500];
    if (score >= 50) return colors.primary[500];
    return colors.redAccent[500];
  };

  const formatDateTime = (dateTime) => {
    return formatToIST(dateTime);
  };

  /** Format time difference for display: "Xh early", "Xh late", "On time", or "—". */
  const formatTimeDiff = (hours) => {
    if (hours == null || Number.isNaN(Number(hours))) return '—';
    const h = Number(hours);
    const abs = Math.abs(h);
    const value = abs % 1 === 0 ? String(Math.round(abs)) : abs.toFixed(1);
    if (h > 0) return `${value}h early`;
    if (h < 0) return `${value}h late`;
    return 'On time';
  };

  const getTimeDiffDisplay = (hours) => {
    const text = formatTimeDiff(hours);
    if (text === '—') return text;
    if (hours != null && !Number.isNaN(Number(hours))) {
      const h = Number(hours);
      if (h > 0) {
        return (
          <Typography
            component="span"
            color="success.main"
            fontWeight="700"
            fontSize="12px"
          >
            {text}
          </Typography>
        );
      }
      if (h < 0) {
        return (
          <Typography
            component="span"
            color="error.main"
            fontWeight="700"
            fontSize="12px"
          >
            {text}
          </Typography>
        );
      }
    }
    return text;
  };

  if (loading) {
    return (
      <Box sx={{ m: { xs: 1.5, sm: 2, md: 3 }, pb: { xs: 4, md: 2 } }}>
        <Header
          title="Booking Intelligence"
          subtitle="AI-Powered Trip-Shipment Matching"
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
            backgroundColor: 'background.paper',
            borderRadius: radiusLg,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: cardShadow,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: 'primary.main' }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Loading matches…
            </Typography>
          </Stack>
        </Box>
      </Box>
    );
  }

  const inputHeight = 44;
  const isDark = theme.palette.mode === 'dark';
  const swiftcarryHover =
    theme.palette.mode === 'dark' ? colors.swiftcarry[100] : '#00a67d';
  const paperSx = {
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    borderRadius: radiusLg,
    boxShadow: cardShadow,
  };
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      minHeight: inputHeight,
      borderRadius: radiusSm,
      backgroundColor: isDark ? colors.primary[500] : '#ffffff',
      fontSize: '0.9375rem',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      '& fieldset': {
        borderColor: 'divider',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: isDark ? colors.swiftcarry[100] : 'rgba(0, 201, 150, 0.4)',
      },
      '&.Mui-focused fieldset': {
        borderWidth: '2px',
        borderColor: isDark ? colors.swiftcarry[100] : 'rgba(0, 201, 150, 0.6)',
        boxShadow: `0 0 0 3px ${
          isDark ? 'rgba(0, 201, 150, 0.15)' : 'rgba(0, 201, 150, 0.12)'
        }`,
      },
      '& input': {
        py: 1.5,
        px: 1.5,
      },
      '&.MuiInputBase-multiline': {
        '& textarea': { py: 1.5, px: 1.5 },
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.9375rem',
    },
  };
  const primaryButtonSx = {
    height: inputHeight,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: radiusSm,
    boxShadow: '0 2px 6px rgba(0, 201, 150, 0.25)',
    bgcolor: colors.swiftcarry[100],
    '&:hover': {
      bgcolor: swiftcarryHover,
      boxShadow: '0 4px 12px rgba(0, 201, 150, 0.35)',
    },
  };
  const secondaryButtonSx = {
    height: inputHeight,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: radiusSm,
    borderColor: 'divider',
    '&:hover': {
      borderColor: 'action.selected',
      bgcolor: 'action.hover',
    },
  };

  return (
    <Box
      sx={{
        m: { xs: 1.5, sm: 2, md: 3 },
        pb: { xs: 4, md: 2 },
      }}
    >
      <Header
        title="Booking Intelligence"
        subtitle="AI-Powered Trip-Shipment Matching"
      />

      {/* Search & Filters in one card */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5, md: 3 },
          borderRadius: radiusLg,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: cardShadow,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            mb: 2,
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: radiusSm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              color: 'primary.main',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <Search sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="700" color="text.primary">
            Search by ID
          </Typography>
        </Box>
        <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Trip ID"
              value={topFilters.tripId}
              onChange={(e) => handleTopFilterChange('tripId', e.target.value)}
              placeholder="Enter Trip ID"
              variant="outlined"
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Shipment ID"
              value={topFilters.shipmentId}
              onChange={(e) =>
                handleTopFilterChange('shipmentId', e.target.value)
              }
              placeholder="Enter Shipment ID"
              variant="outlined"
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Search />}
              onClick={handleTopFilterSearch}
              sx={primaryButtonSx}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Clear />}
              onClick={clearTopFilters}
              sx={secondaryButtonSx}
            >
              Clear
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5, borderColor: 'divider' }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            mb: 2,
            flexWrap: 'wrap',
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: radiusSm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              color: 'primary.main',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <FilterList sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="700" color="text.primary">
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Min Score %"
              type="number"
              value={filters.minScore}
              onChange={(e) => handleFilterChange('minScore', e.target.value)}
              inputProps={{ min: 0, max: 100 }}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Max Score %"
              type="number"
              value={filters.maxScore}
              onChange={(e) => handleFilterChange('maxScore', e.target.value)}
              inputProps={{ min: 0, max: 100 }}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Source City"
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              variant="outlined"
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Destination City"
              value={filters.destination}
              onChange={(e) =>
                handleFilterChange('destination', e.target.value)
              }
              variant="outlined"
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Vehicle Type"
              value={filters.vehicleType}
              onChange={(e) =>
                handleFilterChange('vehicleType', e.target.value)
              }
              variant="outlined"
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Clear />}
              onClick={clearFilters}
              sx={secondaryButtonSx}
            >
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        {filteredMatches.map((match, index) => {
          const scoreColor = getScoreColor(match.matchScore);
          const scoreValue =
            match.matchScore != null ? match.matchScore.toFixed(1) : 'N/A';
          const { tripId: mTripId, shipmentId: mShipmentId } =
            getMatchIds(match);
          const assignKey = `${mTripId}-${mShipmentId}`;
          return (
            <Grid item xs={12} key={index}>
              <Card
                elevation={0}
                variant="outlined"
                sx={{
                  borderRadius: radiusLg,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  boxShadow: cardShadow,
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    borderColor: 'action.selected',
                    boxShadow: cardShadowHover,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  {/* Header: Match # + score chip + Assign — stacks on mobile */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 2,
                      mb: 2,
                      pb: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      flexWrap="wrap"
                      sx={{ flex: { sm: 1 } }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="700"
                        color="text.primary"
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          letterSpacing: '0.01em',
                        }}
                      >
                        Match #{page * pageSize + index + 1}
                      </Typography>
                      <Chip
                        label={`${scoreValue}% match`}
                        size="small"
                        sx={{
                          bgcolor: scoreColor,
                          color: 'white',
                          fontWeight: 700,
                          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                          borderRadius: radiusSm,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                          '& .MuiChip-label': { px: { xs: 1, sm: 1.5 } },
                        }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      size="medium"
                      fullWidth={!isSmUp}
                      startIcon={
                        assignLoading[assignKey] ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <Assignment />
                        )
                      }
                      onClick={() => handleAssign(match)}
                      disabled={assignLoading[assignKey]}
                      sx={{
                        ...primaryButtonSx,
                        minWidth: { sm: 120 },
                      }}
                    >
                      {assignLoading[assignKey] ? 'Assigning…' : 'Assign'}
                    </Button>
                  </Box>

                  <Grid container spacing={{ xs: 2, sm: 2 }}>
                    {/* All components in single line: Match score, Metrics, Trip, Shipment */}
                    <Grid item xs={12} md={3}>
                      <SectionCard
                        icon={
                          <TrendingUp sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        }
                        title="Match score"
                        iconColor={colors.swiftcarry[100]}
                      >
                        <Stack spacing={1.5}>
                          {[
                            { label: 'Pickup', score: match.pickupScore },
                            { label: 'Delivery', score: match.deliveryScore },
                            { label: 'Time', score: match.timeScore },
                            { label: 'Weight', score: match.weightScore },
                          ].map((item, idx) => (
                            <Box key={idx}>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={0.5}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {item.label}
                                </Typography>
                                <Typography variant="caption" fontWeight="600">
                                  {item.score ?? '—'}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={item.score ?? 0}
                                sx={{
                                  height: 8,
                                  borderRadius: radiusSm,
                                  bgcolor: 'action.hover',
                                  overflow: 'hidden',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: getScoreColor(item.score),
                                    borderRadius: radiusSm,
                                  },
                                }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      </SectionCard>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <SectionCard
                        icon={<Scale sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                        title="Metrics"
                        iconColor={colors.redAccent[500]}
                      >
                        <Grid container spacing={2}>
                          <MetricCell
                            label="Vehicle Capacity"
                            value={`${
                              match.tripDetails?.tripCapacityKg ??
                              match.tripCapacityKg ??
                              '—'
                            } kg`}
                          />
                          <MetricCell
                            label="Available Capacity"
                            value={`${
                              match.tripDetails?.remainingCapacity ??
                              match.remainingCapacity ??
                              '0'
                            } kg`}
                          />
                          <MetricCell
                            label="Shipment Weight"
                            value={`${
                              match.shipmentDetails?.totalWeight ??
                              match.shipmentWeightKg ??
                              '—'
                            } kg`}
                          />
                          <MetricCell
                            label="Usage"
                            value={`${match.weightUsagePercent ?? '—'}%`}
                          />
                          <MetricCell
                            label="Pickup Distance"
                            value={`${match.pickupDistanceKm ?? '—'} km`}
                          />
                          <MetricCell
                            label="Delivery Distance"
                            value={`${match.deliveryDistanceKm ?? '—'} km`}
                          />
                          <MetricCell
                            label="Time Difference"
                            value={getTimeDiffDisplay(
                              match.timeDifferenceHours,
                            )}
                          />
                        </Grid>
                      </SectionCard>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <SectionCard
                        icon={
                          <DirectionsCar
                            sx={{ fontSize: { xs: 18, sm: 20 } }}
                          />
                        }
                        title="Trip"
                        iconColor={colors.greenAccent[700]}
                      >
                        <Stack spacing={0.75}>
                          <LabelValue
                            label="ID"
                            value={
                              match.tripDetails?.tripId || match.tripId || '—'
                            }
                          />
                          <LabelValue
                            label="Traveller"
                            value={
                              match.tripDetails?.travelerName ||
                              match.travelerName ||
                              '—'
                            }
                            icon={<Person sx={{ fontSize: '0.875rem' }} />}
                          />
                          <LabelValue
                            label="Phone"
                            value={
                              match.tripDetails?.travelerPhone ||
                              match.travelerPhone ||
                              '—'
                            }
                            icon={<Phone sx={{ fontSize: '0.875rem' }} />}
                          />
                          <LabelValue
                            label="Route"
                            value={`${
                              match.tripDetails?.tripSource || match.tripSource
                            } → ${
                              match.tripDetails?.tripDest || match.tripDest
                            }`}
                            icon={<Route sx={{ fontSize: '0.875rem' }} />}
                          />
                          <LabelValue
                            label="Start"
                            value={formatToIST(
                              match.tripDetails?.tripStartTime ||
                                match.tripStartTime,
                            )}
                            // icon={<AccessTime sx={{ fontSize: '0.875rem' }} />}
                          />
                          <LabelValue
                            label="End"
                            value={formatToIST(
                              match.tripDetails?.tripEndTime ||
                                match.tripEndTime,
                            )}
                            // icon={<AccessTime sx={{ fontSize: '0.875rem' }} />}
                          />
                          <LabelValue
                            label="Vehicle"
                            value={
                              match.tripDetails?.vehicleName ||
                              match.vehicleName ||
                              '—'
                            }
                          />
                        </Stack>
                      </SectionCard>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <SectionCard
                        icon={
                          <LocalShipping
                            sx={{ fontSize: { xs: 18, sm: 20 } }}
                          />
                        }
                        title="Shipment"
                        iconColor={colors.blueAccent[500]}
                      >
                        <Stack spacing={0.75}>
                          <LabelValue
                            label="ID"
                            value={
                              match.shipmentDetails?.shipmentId ||
                              match.shipmentId ||
                              '—'
                            }
                          />
                          <LabelValue
                            label="Sender"
                            value={
                              match.shipmentDetails?.senderName ||
                              match.senderName ||
                              '—'
                            }
                            icon={<Person sx={{ fontSize: '0.875rem' }} />}
                          />
                          <LabelValue
                            label="Phone"
                            value={
                              match.shipmentDetails?.senderPhone ||
                              match.senderPhone ||
                              '—'
                            }
                            icon={<Phone sx={{ fontSize: '0.875rem' }} />}
                          />
                          <LabelValue
                            label="Route"
                            value={`${
                              match.shipmentDetails?.shipmentSource ||
                              match.shipmentSource
                            } → ${
                              match.shipmentDetails?.shipmentDest ||
                              match.shipmentDest
                            }`}
                            icon={<Route sx={{ fontSize: '0.875rem' }} />}
                          />
                          <LabelValue
                            label="Deadline"
                            value={formatDateTime(
                              match.shipmentDetails?.shipmentDeadline ||
                                match.shipmentDeadline,
                            )}
                            // icon={<AccessTime sx={{ fontSize: '0.875rem' }} />}
                          />
                        </Stack>
                      </SectionCard>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {filteredMatches.length === 0 && matches.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            ...paperSx,
            mt: { xs: 2, md: 3 },
            py: { xs: 4, md: 8 },
            px: { xs: 2, md: 3 },
            textAlign: 'center',
          }}
        >
          <Avatar
            sx={{
              width: { xs: 56, md: 72 },
              height: { xs: 56, md: 72 },
              mx: 'auto',
              mb: 2,
              bgcolor: 'action.hover',
              color: 'text.secondary',
              boxShadow: cardShadow,
            }}
          >
            <FilterList sx={{ fontSize: { xs: 28, md: 40 } }} />
          </Avatar>
          <Typography
            variant="h6"
            fontWeight="700"
            color="text.primary"
            mb={1}
            sx={{
              fontSize: { xs: '1rem', sm: '1.25rem' },
              letterSpacing: '0.01em',
            }}
          >
            No matches for current filters
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              maxWidth: 360,
              mx: 'auto',
              px: { xs: 1, md: 0 },
              fontWeight: 500,
            }}
          >
            Try adjusting score range, source, destination, or vehicle type to
            see more results.
          </Typography>
        </Paper>
      )}

      {matches.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            ...paperSx,
            mt: { xs: 2, md: 3 },
            py: { xs: 4, md: 8 },
            px: { xs: 2, md: 3 },
            textAlign: 'center',
          }}
        >
          <Avatar
            sx={{
              width: { xs: 56, md: 72 },
              height: { xs: 56, md: 72 },
              mx: 'auto',
              mb: 2,
              bgcolor: 'action.hover',
              color: 'text.secondary',
              boxShadow: cardShadow,
            }}
          >
            <TrendingUp sx={{ fontSize: { xs: 28, md: 40 } }} />
          </Avatar>
          <Typography
            variant="h6"
            fontWeight="700"
            color="text.primary"
            mb={1}
            sx={{
              fontSize: { xs: '1rem', sm: '1.25rem' },
              letterSpacing: '0.01em',
            }}
          >
            No matches yet
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              maxWidth: 360,
              mx: 'auto',
              px: { xs: 1, md: 0 },
              fontWeight: 500,
            }}
          >
            AI-powered trip–shipment matches will appear here when available.
          </Typography>
        </Paper>
      )}

      {totalCount > 0 && (
        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            mb: { xs: 2, md: 2 },
            borderRadius: radiusLg,
            border: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
          }}
        >
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 20, 50]}
            labelRowsPerPage="Rows per page:"
            sx={{
              '& .MuiTablePagination-toolbar': {
                padding: '16px 24px',
                minHeight: '64px',
                justifyContent: 'space-between',
                alignItems: 'center',
              },
              '& .MuiTablePagination-selectLabel': {
                marginBottom: 0,
                fontSize: '14px',
                fontWeight: 500,
                color: '#666',
              },
              '& .MuiTablePagination-displayedRows': {
                marginBottom: 0,
                fontSize: '14px',
                fontWeight: 500,
                color: '#666',
              },
              '& .MuiTablePagination-select': {
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
                padding: '4px 8px',
                marginLeft: '8px',
                marginRight: '16px',
                backgroundColor: 'white',
                '&:focus': {
                  borderColor: colors.swiftcarry[100],
                  backgroundColor: 'transparent',
                },
              },
              '& .MuiTablePagination-actions': {
                marginLeft: '20px',
                '& .MuiIconButton-root': {
                  padding: '8px',
                  margin: '0 2px',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0',
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: colors.swiftcarry[100],
                    borderColor: colors.swiftcarry[100],
                    color: 'white',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#e0e0e0',
                    color: '#ccc',
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default BookingIntelligence;
